import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  HOME_BUYING_COST_DISCLAIMER,
  type CostLine,
  type HomeBuyingCostOutput,
  fhssBullet,
  formatAud,
  formatRange,
  headline,
  lineBullet,
  sourceBullet,
} from "@aucash/rules/home-buying-cost";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented, YesNo } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// home-buying-cost — the Wave-3 CAPSTONE: the itemised "true cash to buy"
// composer, surfaced stateless-v1 (PLAN arch decision #1). One screen; the
// result renders INLINE (no save-by-id, no auth gate, no persistence). Each
// compute is a stateless `calc.homeBuyingCost.compute` query — input -> output.
//
// The rules module already composes its four children internally
// (stamp-duty + lmi + first-home-scheme + fhss); the screen just calls the one
// procedure and renders the itemised, fully-cited breakdown. Copy comes verbatim
// from the rules layer's copy helpers so the wording can never drift from the
// API/SEO surfaces (validation §3: information, not advice).
//
// REGULATORY POSTURE (binding — NCCP/AFSL): a personalised total-cash-to-buy
// figure is EXACTLY the surface a paid credit handoff must never ride alongside
// (C2 dealing-by-arranging). There is NO mortgage/broker/lender CTA anywhere on
// this screen — the composite no-credit-handoff guard holds at the UI. The LMI
// line stays an indicative RANGE (never a quote); the FHSS releasable amount is
// shown SEPARATELY as the buyer's own super, NOT netted into the completion
// cash. The full disclaimer renders above the first number (§3.2).

const ASSESSMENT_DATE = "2026-03-01"; // FY2025-26 assessment date (passed in,
// never the clock — the in-force child rulesets resolve off this date).

type HbcState = "NSW" | "VIC" | "QLD";
type BuildType = "established" | "new";

const STATE_OPTIONS: ReadonlyArray<{ value: HbcState; label: string }> = [
  { value: "NSW", label: "NSW" },
  { value: "VIC", label: "VIC" },
  { value: "QLD", label: "QLD" },
];

const BUILD_OPTIONS: ReadonlyArray<{ value: BuildType; label: string }> = [
  { value: "established", label: "Established" },
  { value: "new", label: "New build" },
];

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.homeBuyingCost.compute.query>
>;

export default function HomeBuyingCostIndex(): JSX.Element {
  const router = useRouter();

  const [state, setState] = useState<HbcState>("NSW");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [deposit, setDeposit] = useState("");
  const [buildType, setBuildType] = useState<BuildType>("established");
  const [firstHomeBuyer, setFirstHomeBuyer] = useState(true);
  const [income, setIncome] = useState("");

  // FHSS — optional; shown only when the buyer has made voluntary super
  // contributions toward a deposit. Surfaced as their OWN super, not netted.
  const [includeFhss, setIncludeFhss] = useState(false);
  const [fhssAge, setFhssAge] = useState("");
  const [fhssConcessional, setFhssConcessional] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const buildInput = (): Parameters<
    typeof trpc.calc.homeBuyingCost.compute.query
  >[0] => {
    return {
      input: {
        state,
        purchasePrice: num(purchasePrice),
        deposit: num(deposit),
        buildType,
        firstHomeBuyer,
        income: num(income),
        assessmentDate: ASSESSMENT_DATE,
        ...(includeFhss
          ? {
              fhss: {
                ageAtDetermination: Math.trunc(num(fhssAge)),
                concessionalThisYear: num(fhssConcessional),
              },
            }
          : {}),
      },
    };
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (purchasePrice.trim() === "" || num(purchasePrice) <= 0)
      next.price = "Enter the purchase price.";
    if (deposit.trim() === "") next.deposit = "Enter your saved deposit.";
    else if (num(deposit) > num(purchasePrice))
      next.deposit = "Your deposit can't be more than the purchase price.";
    if (includeFhss && fhssAge.trim() === "")
      next.fhssAge = "Enter your age at the planned FHSS release.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.homeBuyingCost.compute
      .query(buildInput())
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Cost to buy" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>FY26 · upfront cash to buy</Eyebrow>
            <Text style={styles.title}>What's the true upfront cost of buying?</Text>
            <Text style={styles.subtitle}>
              Add up the standard upfront costs of buying a home from official
              figures — transfer (stamp) duty, indicative Lenders Mortgage
              Insurance, conveyancing, inspection and registration fees, less any
              First Home Owner Grant. The total is an indicative range, never a
              quote.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="Which state is the property in?">
            <Segmented<HbcState>
              options={STATE_OPTIONS}
              value={state}
              onChange={setState}
              testIDPrefix="state"
            />
          </Field>

          <Field
            label="Purchase price"
            help="The contract price / dutiable value of the property."
            error={errors.price}
          >
            <MoneyInput
              value={purchasePrice}
              onChange={setPurchasePrice}
              testID="input-purchase-price"
              accessibilityLabel="Purchase price"
            />
          </Field>

          <Field
            label="Deposit you've saved"
            help="Drives your loan-to-value ratio and the 5% Deposit Scheme gate."
            error={errors.deposit}
          >
            <MoneyInput
              value={deposit}
              onChange={setDeposit}
              testID="input-deposit"
              accessibilityLabel="Deposit you've saved"
            />
          </Field>

          <Field label="Established home or new build?">
            <Segmented<BuildType>
              options={BUILD_OPTIONS}
              value={buildType}
              onChange={setBuildType}
              testIDPrefix="build"
            />
          </Field>

          <Field label="Is this your first home?">
            <YesNo
              value={firstHomeBuyer}
              onChange={setFirstHomeBuyer}
              testIDPrefix="first-home"
            />
          </Field>

          <Field
            label="Combined annual income (optional)"
            help="Used for the First Home scheme surfaces. Leave blank if you'd rather not say."
          >
            <MoneyInput
              value={income}
              onChange={setIncome}
              accessibilityLabel="Combined annual income"
            />
          </Field>

          <Field label="Have you made voluntary super contributions toward a deposit (FHSS)?">
            <Checkbox
              checked={includeFhss}
              onChange={setIncludeFhss}
              label="Yes — show what I might be able to release under the First Home Super Saver scheme."
            />
          </Field>

          {includeFhss ? (
            <>
              <Field
                label="Your age at the planned FHSS release"
                help="FHSS releases require you to be 18 or over."
                error={errors.fhssAge}
              >
                <MoneyInput
                  value={fhssAge}
                  onChange={setFhssAge}
                  accessibilityLabel="Your age at the planned FHSS release"
                />
              </Field>
              <Field
                label="Voluntary concessional super contributions this year"
                help="Salary-sacrifice or personal-deductible contributions, excluding employer super."
              >
                <MoneyInput
                  value={fhssConcessional}
                  onChange={setFhssConcessional}
                  accessibilityLabel="Voluntary concessional super contributions this year"
                />
              </Field>
            </>
          ) : null}

          <Button
            label={submitting ? "Calculating…" : "See the itemised cost →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="home-buying-cost-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/**
 * Frame the headline total SENSIBLY when government help (FHOG + concessions)
 * exceeds the other upfront costs — the QLD new-build first-home case returns a
 * NEGATIVE "cash to complete" (~−A$27,650). A bare negative range reads like
 * guaranteed cash-back, so we floor the cash-to-complete at A$0 and state, as a
 * cited fact, that the grant exceeds the other upfront costs by the surplus
 * amount. This is presentation only — the rules figure is unchanged, the
 * itemised lines below still show the real (negative FHOG) figures, and the
 * grant is a gated maximum, never a personalised payout. Info-not-advice.
 */
function presentTotal(out: HomeBuyingCostOutput): { lead: string; body: string } {
  const total = out.totalCashToComplete;
  // The whole band sits at or below zero: the grant/concessions more than cover
  // the other upfront costs. Present a A$0 floor + the surplus, not a raw
  // negative that looks like cash-back.
  if (total.high <= 0) {
    const surplus: typeof total = { low: Math.abs(total.high), high: Math.abs(total.low) };
    return {
      lead: `Estimated upfront cash to complete a ${formatAud(out.purchasePrice)} purchase in ${out.state}: about A$0.`,
      body:
        `On these figures the First Home Owner Grant and any duty concession exceed your other ` +
        `upfront costs by roughly ${formatRange(surplus)} (indicative). That surplus is not a cash ` +
        `payment to you — the grant is paid toward the purchase and is a gated maximum, not a payout. ` +
        `It means the standard upfront costs here are effectively covered; see the itemised lines below.`,
    };
  }
  // The band straddles zero — grants may fully cover the other costs.
  if (total.low < 0) {
    return {
      lead: headline(out),
      body:
        `At the low end the First Home Owner Grant and any duty concession may fully cover your ` +
        `other upfront costs. The total is an indicative band, never a single quote, because LMI ` +
        `and several fees are genuine ranges — see the itemised lines below.`,
    };
  }
  return {
    lead: headline(out),
    body:
      "This is the total of the cited line items below — an indicative band, " +
      "never a single quote, because LMI and several fees are genuine ranges.",
  };
}

/** Inline itemised result — stateless, rendered straight under the form. The
 * full disclaimer (rules-layer HOME_BUYING_COST_DISCLAIMER) sits at the TOP,
 * before any number (§3.2 placement rule). Every line is traceable to its
 * source; the FHSS releasable amount is surfaced SEPARATELY (own super, not
 * netted); there is NO mortgage/broker/lender CTA (NCCP composite guard). */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const out = result.output;
  const lines: CostLine[] = [
    out.dutyLine,
    out.lmiLine,
    ...out.miscLines,
    out.fhogLine,
  ];
  const fhss = fhssBullet(out);
  const present = presentTotal(out);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{HOME_BUYING_COST_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · FY26</Eyebrow>
        <Chip label="Indicative range" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{present.lead}</Text>
        <Text style={styles.bodyText}>{present.body}</Text>
      </Card>

      <Section title="The itemised cost, line by line">
        {lines.map((line) => (
          <LineRow key={line.id} line={line} />
        ))}
      </Section>

      {fhss !== null ? (
        <Section title="First Home Super Saver — your own super">
          <Card style={{ gap: space.xs }}>
            <Text style={styles.bodyText}>{fhss}</Text>
          </Card>
        </Section>
      ) : null}

      {out.stackingNotes.length > 0 ? (
        <Section title="How these schemes stack">
          {out.stackingNotes.map((note, i) => (
            <Text key={i} style={styles.bodyText}>
              • {note}
            </Text>
          ))}
        </Section>
      ) : null}

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(result.ruleVersion, result.sourcesVerifiedAt)}
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** One itemised cost line — its amount (or indicative range), label and the
 * cited derivation note. Every figure traceable to its source module. */
function LineRow({ line }: { line: CostLine }): JSX.Element {
  const isHelp = line.amount < 0;
  const shown = line.range
    ? formatRange(line.range)
    : `${isHelp ? "− " : ""}${formatAud(Math.abs(line.amount))}`;
  return (
    <Card style={styles.lineCard}>
      <View style={styles.lineHead}>
        <Text style={styles.lineLabel}>{line.label}</Text>
        <Text style={[styles.lineAmount, isHelp && styles.lineAmountHelp]}>
          {shown}
          {line.indicative ? " (indic.)" : ""}
        </Text>
      </View>
      <Text style={styles.lineNote}>{line.note}</Text>
    </Card>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },

  lineCard: { gap: space.xs },
  lineHead: {
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: space.sm,
  },
  lineLabel: {
    fontFamily: fonts.bodySemibold,
    fontSize: 14.5,
    color: colors.ink,
    flex: 1,
  },
  lineAmount: {
    fontFamily: fonts.bodySemibold,
    fontSize: 15,
    color: colors.ink,
    ...tabular,
  },
  lineAmountHelp: { color: colors.oceanDeep },
  lineNote: { fontFamily: fonts.body, fontSize: 13, lineHeight: 19, color: colors.muted },

  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
